'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import SignupHeader from '@/components/SignupHeader';
import Toast from '@/components/Toast';
import '@/styles/global.css';

export default function UserSignupPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: ''
  });

  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[0-9\s]{10,}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      fullName: '',
      email: '',
      phone: '',
      password: ''
    };

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setToast(null);

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({ message: data.error || 'Something went wrong. Please try again.', type: 'error' });
        setIsLoading(false);
        return;
      }

      // Store token in localStorage
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      setToast({ message: 'Account created successfully!', type: 'success' });
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      
    } catch (error) {
      console.error('Signup error:', error);
      setToast({ message: 'Something went wrong. Please try again.', type: 'error' });
      setIsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <SignupHeader />

      <section className="join-section py-20">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-2 gap-30">
            {/* Left Side */}
            <div className="join-left">
              <p className="eyebrow font-['Archivo',sans-serif] text-[16px] text-[#8A7296] mb-4">
                • Women-only & women-led
              </p>
              <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] leading-[56px] mb-6">
                Join a women-only fitness community
              </h1>
              <p className="desc font-['Archivo',sans-serif] text-[18px] text-[#6e6672] leading-[28px]">
                Connect with certified trainers, discover inclusive gyms and
                reach your goals in a safe, empowering space.
              </p>
            </div>

            {/* Right Side - Create Account Form */}
            <div>
              <form className="signup-card" onSubmit={handleSubmit}>
                <h2>Create your account</h2>

                <label>Full name</label>
                <input 
                  type="text" 
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  className={errors.fullName ? 'border-red-500' : ''}
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                )}

                <label>Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. you@example.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}

                <label>Phone Number</label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g. 000 000 0000"
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}

                <label>Password</label>
                <input 
                  type="password" 
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="e.g. 8+ characters"
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}

                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Creating Account...' : 'Sign Up'}
                  {!isLoading && <Image src="/btn-arrow.svg" width={16} height={16} alt="arrow" className="inline-block ml-2" />}
                </button>
              </form>

              <p className="signin text-center font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] mt-8">
                Already have an account?{' '}
                <Link href="/login" className="text-[#8A7296] hover:text-[#7a647d] transition-colors font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
